import Keycloak from 'keycloak-js';

// Read configuration from Vite environment variables with production defaults
const keycloakUrl = (import.meta.env.VITE_KEYCLOAK_URL || 'https://auth.dhristhi.app').replace(/\/+$/, '');
const realm = import.meta.env.VITE_KEYCLOAK_REALM || 'dpdp-dhristhi';
const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'dpdp-register';

export const isAuthDisabled = import.meta.env.VITE_AUTH_DISABLED === 'true';

export interface KeycloakUserInfo {
  id?: string;
  name: string;
  givenName?: string;
  familyName?: string;
  email: string;
  username: string;
  roles: string[];
}

let keycloakInstance: Keycloak | null = null;
let initPromise: Promise<boolean> | null = null;

/**
 * Returns canonical redirect URI without query strings or hashes.
 */
export function getCanonicalRedirectUri(): string {
  if (typeof window === 'undefined') return 'http://127.0.0.1:5173';
  return `${window.location.origin}${window.location.pathname}`;
}

/**
 * Returns current Keycloak instance, creating a fresh one if null.
 */
export function getKeycloak(): Keycloak {
  if (!keycloakInstance) {
    keycloakInstance = new Keycloak({
      url: keycloakUrl,
      realm: realm,
      clientId: clientId,
    });
  }
  return keycloakInstance;
}

/**
 * Helper to parse JWT payload.
 */
function parseJwtPayload(token: string): Record<string, any> {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return {};
  }
}

/**
 * Generates PKCE code_challenge and code_verifier using Web Crypto API.
 */
async function generatePKCE() {
  const array = new Uint8Array(48);
  crypto.getRandomValues(array);
  const codeVerifier = Array.from(array, dec => ('0' + dec.toString(16)).slice(-2)).join('');

  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);

  const base64Digest = btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  return { codeVerifier, codeChallenge: base64Digest };
}

/**
 * Parses callback parameters from URL hash or query string.
 */
function extractCallbackParams(): { code?: string; state?: string; error?: string; error_description?: string } {
  if (typeof window === 'undefined') return {};

  const params = new URLSearchParams();

  // 1. Check hash fragment (#state=...&code=...)
  if (window.location.hash && window.location.hash.length > 1) {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    for (const [k, v] of hashParams.entries()) {
      params.set(k, v);
    }
  }

  // 2. Check search query (?state=...&code=...)
  if (window.location.search && window.location.search.length > 1) {
    const searchParams = new URLSearchParams(window.location.search);
    for (const [k, v] of searchParams.entries()) {
      if (!params.has(k)) {
        params.set(k, v);
      }
    }
  }

  return {
    code: params.get('code') || undefined,
    state: params.get('state') || undefined,
    error: params.get('error') || undefined,
    error_description: params.get('error_description') || undefined,
  };
}

interface StoredAuthTokens {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  expiresAt: number;
}

const STORAGE_SESSION_KEY = 'kc_auth_session';

function saveStoredTokens(tokens: { access_token: string; refresh_token?: string; id_token?: string; expires_in?: number }) {
  if (typeof window === 'undefined') return;
  const expiresAt = Date.now() + (tokens.expires_in ? tokens.expires_in * 1000 : 300000);
  const data: StoredAuthTokens = {
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
    idToken: tokens.id_token,
    expiresAt,
  };
  try {
    sessionStorage.setItem(STORAGE_SESSION_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('[DPDP Auth] Failed to save session tokens:', e);
  }
}

function getStoredTokens(): StoredAuthTokens | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

function clearStoredTokens() {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(STORAGE_SESSION_KEY);
  } catch (e) {
    // Ignore
  }
}

function getTokenEndpointUrl(): string {
  return `${keycloakUrl}/realms/${realm}/protocol/openid-connect/token`;
}

/**
 * Direct HTTPS refresh token call to Keycloak.
 */
async function refreshTokensDirect(refreshToken: string) {
  const tokenEndpoint = getTokenEndpointUrl();
  console.log('%c[DPDP Auth] 🔄 Refreshing access token via:', 'color: #4f46e5; font-weight: bold;', tokenEndpoint);
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: clientId,
    refresh_token: refreshToken,
  });

  const response = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    },
    body: body.toString(),
  });

  if (!response.ok) {
    const errText = await response.text();
    console.warn('[DPDP Auth] Token refresh failed:', response.status, errText);
    clearStoredTokens();
    throw new Error(`Token refresh failed (${response.status}): ${errText}`);
  }

  const newTokens = await response.json();
  saveStoredTokens(newTokens);
  return newTokens;
}

/**
 * Performs direct token exchange with Keycloak over HTTPS with PKCE verifier.
 */
async function exchangeCodeForTokens(code: string, state: string, redirectUri: string) {
  const storageKey = 'kc-callback-' + state;
  const storedJson = localStorage.getItem(storageKey);
  let pkceCodeVerifier: string | undefined;
  let storedRedirectUri: string | undefined;

  if (storedJson) {
    try {
      const parsed = JSON.parse(storedJson);
      pkceCodeVerifier = parsed.pkceCodeVerifier;
      storedRedirectUri = parsed.redirectUri;
    } catch (e) {
      console.warn('[DPDP Auth] Could not parse stored callback state:', e);
    }
  }

  const effectiveRedirectUri = storedRedirectUri || redirectUri;
  const tokenEndpoint = getTokenEndpointUrl();

  console.log('%c[DPDP Auth] 🔄 Executing token exchange with Keycloak...', 'color: #4f46e5; font-weight: bold;', {
    endpoint: tokenEndpoint,
    code: code.slice(0, 10) + '...',
    redirectUri: effectiveRedirectUri,
    hasVerifier: !!pkceCodeVerifier,
  });

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: clientId,
    code: code,
    redirect_uri: effectiveRedirectUri,
  });

  if (pkceCodeVerifier) {
    body.append('code_verifier', pkceCodeVerifier);
  }

  const response = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    },
    body: body.toString(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: response.statusText }));
    console.error('%c[DPDP Auth] ❌ Token endpoint returned error:', 'color: #ef4444; font-weight: bold;', response.status, errorData);
    localStorage.removeItem(storageKey);
    throw new Error(errorData.error_description || errorData.error || `Token exchange failed (${response.status})`);
  }

  const tokens = await response.json();
  console.log('%c[DPDP Auth] 🎉 Token exchange successful!', 'color: #10b981; font-weight: bold;', tokens);

  // Save session tokens for persistence across reloads
  saveStoredTokens(tokens);

  // Clean up localStorage callback state
  localStorage.removeItem(storageKey);

  return tokens;
}

/**
 * Initializes Keycloak with Authorization Code Flow & PKCE (S256).
 */
export async function initializeKeycloak(): Promise<boolean> {
  if (isAuthDisabled) {
    console.info('[Auth] Keycloak authentication is disabled via VITE_AUTH_DISABLED=true');
    return true;
  }

  const canonicalRedirect = getCanonicalRedirectUri();
  const callback = extractCallbackParams();

  // If OAuth error was returned in URL
  if (callback.error) {
    console.error('[DPDP Auth] OAuth error in URL:', callback.error, callback.error_description);
    window.history.replaceState({}, document.title, canonicalRedirect);
    throw new Error(`${callback.error}: ${callback.error_description || 'OAuth authorization failed'}`);
  }

  const kc = getKeycloak();

  // Already initialized successfully
  if (kc.authenticated !== undefined && kc.authenticated) {
    return true;
  }

  // If initialization is already in progress, reuse the existing promise
  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    try {
      // 1. If returning from Keycloak with code and state, perform direct HTTPS token exchange
      if (callback.code && callback.state) {
        console.log('%c[DPDP Auth] 📥 Detected OAuth Callback Code in URL:', 'color: #4f46e5; font-weight: bold;', callback);
        try {
          const tokens = await exchangeCodeForTokens(callback.code, callback.state, canonicalRedirect);

          // Clean URL fragment / search query
          window.history.replaceState({}, document.title, canonicalRedirect);

          // Initialize keycloak with pre-fetched tokens
          try {
            await kc.init({
              token: tokens.access_token,
              refreshToken: tokens.refresh_token,
              idToken: tokens.id_token,
              checkLoginIframe: false,
            });
          } catch (initErr) {
            console.warn('[DPDP Auth] kc.init warning (proceeding with manual hydration):', initErr);
          }

          // Set properties manually
          kc.token = tokens.access_token;
          kc.refreshToken = tokens.refresh_token;
          kc.idToken = tokens.id_token;
          kc.tokenParsed = parseJwtPayload(tokens.access_token);
          kc.idTokenParsed = tokens.id_token ? parseJwtPayload(tokens.id_token) : undefined;
          kc.authenticated = true;

          console.log('%c[DPDP Auth] ✅ User authenticated via direct token exchange!', 'color: #10b981; font-weight: bold;', kc.tokenParsed);

          return true;
        } catch (exchangeErr) {
          console.warn('[DPDP Auth] Code exchange failed (expired/invalid code). Cleaning URL:', exchangeErr);
          window.history.replaceState({}, document.title, canonicalRedirect);
          // Return false so user sees clean Landing Page
          return false;
        }
      }

      // 2. Check if we have active tokens stored in session
      const stored = getStoredTokens();
      if (stored && stored.accessToken) {
        let validAccessToken = stored.accessToken;
        let validRefreshToken = stored.refreshToken;
        let validIdToken = stored.idToken;

        // If access token is about to expire (< 60s) and we have refresh token, refresh now
        if (stored.expiresAt && Date.now() > stored.expiresAt - 60000 && stored.refreshToken) {
          try {
            const refreshed = await refreshTokensDirect(stored.refreshToken);
            validAccessToken = refreshed.access_token;
            validRefreshToken = refreshed.refresh_token || validRefreshToken;
            validIdToken = refreshed.id_token || validIdToken;
          } catch (refreshErr) {
            console.warn('[DPDP Auth] Session refresh failed, clearing session:', refreshErr);
            clearStoredTokens();
            return false;
          }
        }

        try {
          await kc.init({
            token: validAccessToken,
            refreshToken: validRefreshToken,
            idToken: validIdToken,
            checkLoginIframe: false,
          });
        } catch (initErr) {
          console.warn('[DPDP Auth] kc.init warning on restore:', initErr);
        }

        kc.token = validAccessToken;
        kc.refreshToken = validRefreshToken;
        kc.idToken = validIdToken;
        kc.tokenParsed = parseJwtPayload(validAccessToken);
        kc.idTokenParsed = validIdToken ? parseJwtPayload(validIdToken) : undefined;
        kc.authenticated = true;

        console.log('%c[DPDP Auth] ✅ Restored active session from storage!', 'color: #10b981; font-weight: bold;', kc.tokenParsed);
        return true;
      }

      // 3. If no active session and no code in URL, do not auto-redirect; return false to display Landing Screen
      console.log('%c[DPDP Auth] ℹ️ No active session found. Ready for user login.', 'color: #64748b;');
      return false;
    } catch (error) {
      // Reset instance on error so retry creates clean object
      keycloakInstance = null;
      initPromise = null;
      clearStoredTokens();
      console.error('%c[DPDP Auth] ❌ Keycloak initialization failed:', 'color: #ef4444; font-weight: bold;', error);
      throw error;
    }
  })();

  return initPromise;
}

/**
 * Extracts and formats user details from the parsed JWT ID / Access Token.
 */
export function extractUserInfo(): KeycloakUserInfo | null {
  if (isAuthDisabled) {
    return {
      id: 'usr-offline-admin',
      name: 'Compliance Officer (Offline Mode)',
      givenName: 'Compliance',
      familyName: 'Officer',
      email: 'admin@dhristhi.local',
      username: 'compliance-admin',
      roles: ['dpo', 'legal_counsel', 'admin'],
    };
  }

  const kc = getKeycloak();
  if (!kc.authenticated) {
    return null;
  }

  const token = (kc.tokenParsed || (kc.token ? parseJwtPayload(kc.token) : {})) as Record<string, any>;
  const idToken = (kc.idTokenParsed || (kc.idToken ? parseJwtPayload(kc.idToken) : {})) as Record<string, any>;

  const realmRoles = token.realm_access?.roles || [];
  const clientRoles = token.resource_access?.[clientId]?.roles || [];
  const allRoles = Array.from(new Set([...realmRoles, ...clientRoles])).filter(
    (role: string) => !role.startsWith('default-roles-') && role !== 'offline_access' && role !== 'uma_authorization'
  );

  const givenName = idToken.given_name || token.given_name || '';
  const familyName = idToken.family_name || token.family_name || '';
  const fullName = idToken.name || token.name || (givenName || familyName ? `${givenName} ${familyName}`.trim() : token.preferred_username || 'Authorized User');

  return {
    id: token.sub || idToken.sub,
    name: fullName,
    givenName,
    familyName,
    email: idToken.email || token.email || `${token.preferred_username || 'user'}@dhristhi.app`,
    username: token.preferred_username || idToken.preferred_username || 'user',
    roles: allRoles,
  };
}

/**
 * Trigger robust PKCE login redirect compatible with keycloak-js callback parser.
 */
export async function login(): Promise<void> {
  if (isAuthDisabled) return;

  const canonicalRedirect = getCanonicalRedirectUri();

  try {
    // 1. Generate PKCE Verifier & Challenge
    const { codeVerifier, codeChallenge } = await generatePKCE();
    const state = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2);
    const nonce = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2);

    // 2. Store callback state in localStorage using Keycloak-JS exact storage format
    const callbackState = {
      state,
      nonce,
      redirectUri: canonicalRedirect,
      pkceCodeVerifier: codeVerifier,
      expires: Date.now() + 3600000,
    };

    localStorage.setItem('kc-callback-' + state, JSON.stringify(callbackState));

    // 3. Construct standard Keycloak OIDC Authorization URL with PKCE
    const authUrl = new URL(`${keycloakUrl}/realms/${realm}/protocol/openid-connect/auth`);
    authUrl.searchParams.set('client_id', clientId);
    authUrl.searchParams.set('redirect_uri', canonicalRedirect);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('response_mode', 'fragment');
    authUrl.searchParams.set('scope', 'openid profile email');
    authUrl.searchParams.set('state', state);
    authUrl.searchParams.set('nonce', nonce);
    authUrl.searchParams.set('code_challenge', codeChallenge);
    authUrl.searchParams.set('code_challenge_method', 'S256');

    console.log('%c[DPDP Auth] 🔑 Generated PKCE state and redirecting to Keycloak:', 'color: #4f46e5; font-weight: bold;', {
      state,
      redirectUri: canonicalRedirect,
      authUrl: authUrl.toString(),
    });

    window.location.assign(authUrl.toString());
  } catch (error) {
    console.error('%c[DPDP Auth] ❌ Login redirect failed:', 'color: #ef4444; font-weight: bold;', error);
  }
}

/**
 * Trigger explicit logout redirect and invalidate SSO session.
 */
export async function logout(): Promise<void> {
  if (isAuthDisabled) {
    window.location.reload();
    return;
  }

  const canonicalRedirect = getCanonicalRedirectUri();
  const kc = getKeycloak();
  const idToken = kc.idToken;

  try {
    kc.clearToken();
  } catch (e) {
    // Ignore
  }

  // Reset instance and local callback states
  clearStoredTokens();
  keycloakInstance = null;
  initPromise = null;

  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('kc-callback-')) {
        localStorage.removeItem(key);
      }
    }
  } catch (e) {
    // Ignore
  }

  // Direct OIDC End-Session Endpoint redirect
  const logoutUrl = new URL(`${keycloakUrl}/realms/${realm}/protocol/openid-connect/logout`);
  logoutUrl.searchParams.set('client_id', clientId);
  logoutUrl.searchParams.set('post_logout_redirect_uri', canonicalRedirect);
  if (idToken) {
    logoutUrl.searchParams.set('id_token_hint', idToken);
  }
  window.location.assign(logoutUrl.toString());
}

/**
 * Retrieve current valid access token (refreshes if close to expiring).
 */
export async function getAccessToken(): Promise<string | undefined> {
  if (isAuthDisabled) return 'offline-mock-token';
  const kc = getKeycloak();
  if (!kc.authenticated) return undefined;

  try {
    const stored = getStoredTokens();
    if (stored && stored.refreshToken && stored.expiresAt && Date.now() > stored.expiresAt - 45000) {
      const refreshed = await refreshTokensDirect(stored.refreshToken);
      kc.token = refreshed.access_token;
      kc.refreshToken = refreshed.refresh_token || stored.refreshToken;
      kc.tokenParsed = parseJwtPayload(refreshed.access_token);
      return refreshed.access_token;
    }
    return kc.token;
  } catch (error) {
    console.error('[DPDP Auth] Failed to acquire valid access token:', error);
    return kc.token;
  }
}

export default getKeycloak;
